import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent rounded-xl border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors";

  return (
    <div className="min-h-screen bg-paper flex flex-col" data-testid="login-page">
      <header className="w-full mx-auto px-4 md:px-8 lg:px-12 py-6 border-b border-line flex items-center justify-between w-full">
        <Link to="/" className="flex items-center gap-2">
          <img src="/fluenex-symbol.svg" alt="" aria-hidden="true" className="h-8 md:h-9 w-auto object-contain" />
          <span className="font-display font-bold text-base md:text-lg tracking-tight text-ink">Fluenex <span className="font-normal">Academy</span></span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 font-sans text-sm font-bold text-graphite hover:text-emerald transition-colors">
          <ArrowLeft size={16} /> Back to site
        </Link>
      </header>

      <main className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:block relative gradient-brand overflow-hidden">
          <div className="ambient-glow" />
          <div className="relative h-full flex flex-col justify-end p-12 text-white">
            <p className="font-serif italic text-4xl text-amber leading-snug max-w-md">
              The leads dashboard for enquiries and workshop registrations.
            </p>
            <p className="font-sans text-xs font-bold text-mint mt-6 uppercase tracking-wide">Fluenex Academy · Team access</p>
          </div>
        </div>

        <div className="flex items-center justify-center px-4 py-16">
          <div className="w-full max-w-md">
            <p className="font-sans text-xs font-bold text-terracotta mb-4 uppercase tracking-wide">Team access</p>
            <h1 className="font-display font-bold tracking-tight text-5xl leading-[1.0] text-ink">Sign in</h1>
            <form onSubmit={submit} className="mt-10 space-y-4 bg-surface border border-line rounded-3xl shadow-sm p-6 md:p-8">
              <label className="block">
                <span className="font-sans text-xs font-bold text-graphite mb-2 block uppercase tracking-wide">Email</span>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className={inputCls} placeholder="admin@fluenex.academy" />
              </label>
              <label className="block">
                <span className="font-sans text-xs font-bold text-graphite mb-2 block uppercase tracking-wide">Password</span>
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className={inputCls} placeholder="••••••••" />
              </label>
              {error && <p className="text-sm text-destructive font-sans" data-testid="login-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="login-submit" className="w-full rounded-full bg-terracotta text-white font-bold text-base px-6 py-4 shadow-md hover:bg-terracottahover hover:shadow-glowwarm transition duration-300 disabled:opacity-50">
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
